import { useState } from 'react';
import Button from '../../../components/Button';
import type { VoiceOption } from '../services/settingsTypes';
import { VOICE_ICONS } from './voiceIcons';

interface VoiceEditModalProps {
    voice: VoiceOption | null;
    onSave: (voice: VoiceOption) => Promise<void>;
    onClose: () => void;
}

const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed';

export const VoiceEditModal = ({ voice, onSave, onClose }: VoiceEditModalProps) => {
    const isNew = voice === null;
    const [id, setId] = useState(voice?.id ?? '');
    const [name, setName] = useState(voice?.name ?? '');
    const [description, setDescription] = useState(voice?.description ?? '');
    const [icon, setIcon] = useState(voice?.icon ?? '');
    const [voiceId, setVoiceId] = useState(voice?.voiceId ?? '');
    // New voices stay hidden until they have a preview.
    const [isPublished, setIsPublished] = useState(voice?.isPublished ?? false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!/^[a-z0-9-]+$/.test(id)) {
            setError('ID must contain only lowercase letters, numbers, and hyphens');
            return;
        }
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        if (!/^[A-Za-z0-9]+$/.test(voiceId.trim())) {
            setError('ElevenLabs voice ID must contain only letters and numbers');
            return;
        }

        const trimmedVoiceId = voiceId.trim();
        try {
            setIsSaving(true);
            await onSave({
                id,
                name: name.trim(),
                description: description.trim(),
                icon,
                voiceId: trimmedVoiceId,
                // The old preview is of another voice.
                previewPath: voice?.voiceId === trimmedVoiceId ? voice.previewPath : '',
                isPublished,
                // saveVoiceOptions sets the order from the list position.
                order: voice?.order ?? 0,
            });
        } catch (err) {
            console.error('Failed to save voice:', err);
            setError(err instanceof Error ? err.message : 'Failed to save voice');
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'Add Voice' : 'Edit Voice'}
                    </h3>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                            {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="voiceOptionId" className="block text-sm font-medium text-gray-700 mb-1">ID *</label>
                        <input
                            type="text"
                            id="voiceOptionId"
                            value={id}
                            onChange={(e) => setId(e.target.value)}
                            disabled={!isNew}
                            className={inputClassName}
                            placeholder="e.g., calm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Saved on each user who chooses this voice, so it cannot be changed after creation.
                        </p>
                    </div>

                    <div>
                        <label htmlFor="voiceName" className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                            type="text"
                            id="voiceName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={inputClassName}
                            placeholder="e.g., Calm"
                        />
                        <p className="text-xs text-gray-500 mt-1">Shown to users in Voice settings and in the voice drawer of a call.</p>
                    </div>

                    <div>
                        <label htmlFor="voiceDescription" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            id="voiceDescription"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className={inputClassName}
                            placeholder="e.g., Soft and slow"
                            rows={2}
                        />
                        <p className="text-xs text-gray-500 mt-1">Optional. Shown under the name in the voice drawer of a call.</p>
                    </div>

                    <div>
                        <span className="block text-sm font-medium text-gray-700 mb-1">Icon</span>
                        <div className="flex flex-wrap gap-1">
                            {Object.entries(VOICE_ICONS).map(([key, Icon]) => (
                                <button
                                    key={key}
                                    type="button"
                                    title={key}
                                    aria-label={key}
                                    aria-pressed={icon === key}
                                    onClick={() => setIcon(key)}
                                    className={`p-2 rounded-md border ${icon === key
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    <Icon size={18} />
                                </button>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Shown above the name in Voice settings.</p>
                    </div>

                    <div>
                        <label htmlFor="voiceId" className="block text-sm font-medium text-gray-700 mb-1">ElevenLabs voice ID *</label>
                        <input
                            type="text"
                            id="voiceId"
                            value={voiceId}
                            onChange={(e) => setVoiceId(e.target.value)}
                            className={inputClassName}
                            placeholder="e.g., 21m00Tcm4TlvDq8ikWAM"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Users never see it. A change applies to every user of this voice from their next call, and needs a new preview.
                        </p>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="voiceIsPublished"
                            checked={isPublished}
                            onChange={(e) => setIsPublished(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="voiceIsPublished" className="ml-2 block text-sm text-gray-700">
                            Published (Pro users can choose it)
                        </label>
                    </div>
                </form>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmit} loading={isSaving} disabled={isSaving}>
                        {isNew ? 'Add Voice' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        </div>
    );
};
